<template>
  <nav v-if="0 < totalResults">

    <!-- mobile navigation -->
    <ul
      :class="[
        'pagination-theme pagination-theme--mobile',
        shadowAgainstWhiteBackground ? 'pagination-theme--shadow-against-white-background' : '',
        shadowAgainstBlueBackground ? 'pagination-theme--shadow-against-blue-background' : '',
      ]"
    >

      <!-- previous page button. Example: <  -->
      <li
        v-if="showNextPreviousButton"
        :class="[
          'pagination-theme__li-single',
          1 === currentPageNumber ? 'pagination-theme__li-single--not-interactive' : 'cursor-pointer',
        ]"
      >
        <a @click="1 === currentPageNumber ? '' : pageUpdate(currentPageNumber - 1)">
          <img
            :src="$asset('img/common/pagination/arrow-to-left.svg')"
            :alt="$t('messages_nl.images.arrowLeft')"
            width="8"
            height="30"
            loading="lazy"
          >
          <p>{{ this.textPreviousButton || $t('messages_nl.includes.pagination._previous') }}</p>
        </a>
      </li>

      <!-- next page button. Example: >   -->
      <li
        v-if="showNextPreviousButton"
        :class="[
          'pagination-theme__li-single',
          lastPage === currentPageNumber ? 'pagination-theme__li-single--not-interactive' : 'cursor-pointer',
        ]"
      >
        <a @click="lastPage === currentPageNumber ? '' : pageUpdate(currentPageNumber + 1)">
          <p>{{ this.textNextButton || $t('messages_nl.includes.pagination._next') }}</p>
          <img
            :src="$asset('img/common/pagination/arrow-to-right.svg')"
            :alt="$t('messages_nl.images.arrowRight')"
            width="8"
            height="30"
            loading="lazy"
          >
        </a>
      </li>
    </ul>

    <!-- desktop navigation -->
    <ul
      v-if="resultsPerPage < totalResults"
      :class="[
        'pagination-theme pagination-theme--desktop',
        shadowAgainstWhiteBackground ? 'pagination-theme--shadow-against-white-background' : '',
        shadowAgainstBlueBackground ? 'pagination-theme--shadow-against-blue-background' : '',
      ]"
    >

      <!-- first page button. Example: << -->
      <li
        v-if="showFirstLastButton"
        :class="[
          'pagination-theme__li-single',
          true === atStart ? 'pagination-theme__li-single--not-interactive' : 'pagination-theme__li-single--interactive',
        ]"
      >
        <a @click="true === atStart ? '' : pageUpdate(1)">
          <img
            :src="$asset('img/common/pagination/arrow-double-to-left.svg')"
            :alt="$t('messages_nl.images.doubleArrowLeft')"
            width="14"
            height="30"
            loading="lazy"
          >
        </a>
      </li>

      <!-- previous page button. Example: < -->
      <li
        v-if="showNextPreviousButton"
        :class="[
          'pagination-theme__li-single',
          1 === currentPageNumber ? 'pagination-theme__li-single--not-interactive' : 'pagination-theme__li-single--interactive',
        ]"
      >
        <a @click="1 === currentPageNumber ? '' : pageUpdate(currentPageNumber - 1)">
          <img
            :src="$asset('img/common/pagination/arrow-to-left.svg')"
            :alt="$t('messages_nl.images.arrowLeft')"
            width="8"
            height="30"
            loading="lazy"
          >
        </a>
      </li>

      <!-- pages -->
      <li class="pagination-theme__li-group">
        <ul>

          <!-- first page-->
          <li
            v-if="showFirstLastNumbers && !atStart"
            class="pagination-theme__li-group__li--interactive"
          >
            <a @click="pageUpdate(1)">1</a>
          </li>

          <!-- dots start-->
          <li
            v-if="showFirstLastNumbers && !atStart"
            class="pagination-theme__li-group__li--dots"
          >
            <a>...</a>
          </li>

          <!-- pages-->
          <li
            v-if="showPages"
            v-for="page in pageNumberRange"
            :key="page"
            :class="page === currentPageNumber ? 'pagination-theme__li-group__li--active' : 'pagination-theme__li-group__li--interactive'"
          >
            <a @click="page === currentPageNumber ? '' : pageUpdate(page)">{{ page }}</a>
          </li>

          <!-- dots end-->
          <li
            v-if="showFirstLastNumbers && !atEnd"
            class="pagination-theme__li-group__li--dots"
          >
            <a>...</a>
          </li>

          <!-- last page-->
          <li
            v-if="showFirstLastNumbers && !atEnd"
            class="pagination-theme__li-group__li--interactive"
          >
            <a @click="pageUpdate(lastPage)">{{ lastPage }}</a>
          </li>
        </ul>
      </li>

      <!-- next page button. Example: >   -->
      <li
        v-if="showNextPreviousButton"
        :class="[
          'pagination-theme__li-single',
          lastPage === currentPageNumber ? 'pagination-theme__li-single--not-interactive' : 'pagination-theme__li-single--interactive',
        ]"
      >
        <a @click="lastPage === currentPageNumber ? '' : pageUpdate(currentPageNumber + 1)">
          <img
            :src="$asset('img/common/pagination/arrow-to-right.svg')"
            :alt="$t('messages_nl.images.arrowRight')"
            width="8"
            height="30"
            loading="lazy"
          >
        </a>
      </li>

      <!-- last page button. Example: >>   -->
      <li
        v-if="showFirstLastButton"
        :class="[
          'pagination-theme__li-single',
          true === atEnd ? 'pagination-theme__li-single--not-interactive' : 'pagination-theme__li-single--interactive',
        ]"
      >
        <a @click="true === atEnd ? '' : pageUpdate(lastPage)">
          <img
            :src="$asset('img/common/pagination/arrow-double-to-right.svg')"
            :alt="$t('messages_nl.images.doubleArrowRight')"
            width="14"
            height="30"
            loading="lazy"
          >
        </a>
      </li>
    </ul>
  </nav>
</template>

<script>
// full pagination looks like this: [previous button][first page]...[pageNr 1][pageNr 2][pageNr 3][pageNr 4][pageNr 5]...[last page][next button]
import scrollIntoView from "smooth-scroll-into-view-if-needed";
import { mapGetters } from "vuex";

export default {
  name: "adivare-pagination",
  props: {
    // current page
    currentPageNumber: {
      type: Number,
      required: true,
    },
    // total results
    totalResults: {
      type: Number,
      required: true,
    },
    // total results per page
    resultsPerPage: {
      type: Number,
      required: true,
    },
    // show page number buttons
    showPages: {
      type: Boolean,
      default: false,
      required: false,
    },
    // max total page numbers shown between next/previous buttons and between first/last page buttons. Only odd numbers are allowed
    pagesTotalNumbers: {
      type: Number,
      default: 5,
      required: false,
      validator: (value) => {
        return value % 2 === 1;
      }
    },
    // show next previous buttons
    showNextPreviousButton: {
      type: Boolean,
      default: false,
      required: false,
    },
    // text next button. Mobile only.
    textNextButton: {
      type: String,
      default: null,
      required: false,
    },
    // text previous button. Mobile only.
    textPreviousButton: {
      type: String,
      default: null,
      required: false,
    },
    // show first last page buttons
    showFirstLastButton: {
      type: Boolean,
      default: false,
      required: false,
    },
    // show first last page numbers
    showFirstLastNumbers: {
      type: Boolean,
      default: false,
      required: false,
    },
    shadowAgainstBlueBackground: {
      type: Boolean,
      default: false,
      required: false,
    },
    shadowAgainstWhiteBackground: {
      type: Boolean,
      default: false,
      required: false,
    },
    // enable scroll to top after page change
    enableScrollToTop: {
      type: Boolean,
      default: false,
      required: false,
    },
    // if scroll to top is enabled, choose which prop to watch for changes. Default is currentPageNumber.
    watchProp: {
      type: String,
      default: 'currentPageNumber',
      required: false,
    },
    // if scroll to top is enabled, choose to which id it has to scroll to. Default is scroll to top of body.
    scrollToId: {
      type: String,
      default: null,
      required: false,
    },
  },
  emits: [
    'page-change',
  ],
  data() {
    return {
      lastPage: Math.ceil(this.totalResults / this.resultsPerPage),
      atStart: false,  // pagination at start. When first page is still visible in pagination: [1][2][3][4][5]
      atEnd: false,    // pagination at end. When last page (for example: 82) is still visible in pagination: [78][79][80][81][82]
      enable: true,    // enable pagination
    }
  },
  computed: {
    ...mapGetters([
      'cancellationReviews',
    ]),
    pageNumberRange() {
      // single page
      if (false === this.enable) return [];

      // when total pages do not exceed this.conf.pageNumbers: [1...lastPage]
      if (this.pagesTotalNumbers >= this.lastPage) {

        this.atStart = this.atEnd = true;

        return Array.from(Array(this.lastPage)).map((e, i) => i + 1)
      }

      // first pages: [1...this.conf.pageNumbers]
      if (this.currentPageNumber <= Math.ceil(this.pagesTotalNumbers / 2) || this.currentPageNumber > this.lastPage) {
        this.atStart = true;
        this.atEnd = false;

        return Array.from(Array(this.pagesTotalNumbers)).map((e, i) => i + 1)
      }

      // last pages: [(lastPage - this.conf.pageNumbers)...lastPage]
      if ((this.lastPage - this.currentPageNumber) < Math.ceil(this.pagesTotalNumbers / 2)) {
        this.atStart = false;
        this.atEnd = true;

        return Array.from(Array(this.pagesTotalNumbers)).map((e, i) => i + 1 + this.lastPage - this.pagesTotalNumbers);
      }

      // in between pages
      this.atStart = this.atEnd = false;

      return Array.from(Array(this.pagesTotalNumbers)).map((e, i) => i + this.currentPageNumber - Math.floor(this.pagesTotalNumbers / 2));
    },
  },
  methods: {
    autoScrollToTop() {
      if (false === this.enableScrollToTop) return;

      const scrollOptions = {
        top: 0,
        left: 0,
        behavior: 'smooth',
        block: 'start',
      };

      if (null === this.scrollToId) {
        document.documentElement.scrollTo(scrollOptions);
        document.body.scrollTo(scrollOptions);

        return;
      }

      const element = document.getElementById(this.scrollToId);
      if (null === element) {
        console.error(`Element with id ${this.scrollToId} does not exist. Scroll to element is therefore disabled`);

        return;
      }

      scrollIntoView(element, scrollOptions);
    },
    pageUpdate(page) {
      this.$emit('page-change', {
        page: page,
      });
      const unwatch = this.$watch(this.watchProp, () => {
        this.autoScrollToTop();
        unwatch();
      });
    }
  }
}
</script>
